import { useMemo, useState } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { CustomModal } from '@/shared/components/ui/modal';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip';
import { useCollectionStore } from '../../stores/useCollectionStore';
import { useCollections } from '../../context/CollectionsContext';
import CollectionTitle from './Title';

type CollectionDetailHeaderProps = {
  disabled?: boolean;
  onBackClick: () => void;
};

const CollectionDetailHeader = ({ onBackClick, disabled = false }: CollectionDetailHeaderProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const editable = usePageStore(state => state.editable);
  const currentCollection = useCollectionStore(state => state.currentCollection);
  const canWrite = useMemo(
    () => !disabled && editable && !currentCollection?.archivedAt,
    [currentCollection?.archivedAt, disabled, editable]
  );

  if (!currentCollection) return null;

  return (
    <>
      <div className="w-full flex items-center gap-2 px-3 mb-2">
        <button
          title="Back to collections"
          onClick={onBackClick}
          className={cn('h-6 w-6 rounded-sm', 'text-foreground hover:bg-muted transition-opacity')}
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div
          className={cn(
            'ml-[-4px] flex flex-1 cursor-pointer overflow-x-hidden',
            (currentCollection.archivedAt || !canWrite) && 'cursor-default'
          )}
          onClick={() => {
            if (currentCollection.archivedAt || !canWrite) {
              return;
            }
            setIsModalOpen(true);
          }}
        >
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    'py-4 w-[80%] text-[16px] font-bold outline-none truncate',
                    currentCollection.archivedAt && 'line-through'
                  )}
                >
                  {currentCollection.title}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Click to update "{currentCollection.title}"</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <TitleUpdateModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
      />
    </>
  );
};

export default CollectionDetailHeader;

const TitleUpdateModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const currentCollection = useCollectionStore(state => state.currentCollection);
  const { updateCollection } = useCollections();

  return (
    <CustomModal isOpen={isOpen} onClose={onClose}>
      <div className="pt-4 px-4 pb-4">
        <h4 className="pl-1">Update collection title</h4>
        <CollectionTitle
          title={currentCollection?.title || 'Untitled Collection'}
          placeholder="Untitled Collection"
          onComplete={onClose}
          onChange={val => {
            if (!currentCollection) return;
            void updateCollection(currentCollection.id, { title: val });
          }}
        />

        <h4 className="pl-1">Update collection description</h4>
        <CollectionTitle
          title={currentCollection?.description || ''}
          placeholder="Add a description for your collection"
          autofocus={false}
          bold={false}
          onComplete={onClose}
          onChange={val => {
            if (!currentCollection) return;
            void updateCollection(currentCollection.id, { description: val });
          }}
        />
        <small className="pl-1">Click "Enter" to submit</small>
      </div>
    </CustomModal>
  );
};
